/**
 * Button Components with enhanced interactions
 */

import { forwardRef } from 'react'
import { cn } from '@/lib/utils'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      iconPosition = 'left',
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const baseClasses = cn(
      'inline-flex items-center justify-center font-medium rounded-lg',
      'transition-all duration-150 ease-out',
      'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background',
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
      'active:scale-[0.97]'
    )

    const variantClasses = {
      primary: 'bg-primary text-white hover:bg-primary-hover focus:ring-primary shadow-sm hover:shadow',
      secondary: 'bg-surface text-text-primary border border-border hover:bg-elevated focus:ring-primary',
      ghost: 'bg-transparent text-text-secondary hover:text-text-primary hover:bg-elevated focus:ring-primary',
      danger: 'bg-error text-white hover:bg-red-600 focus:ring-error shadow-sm',
      success: 'bg-success text-white hover:bg-green-600 focus:ring-success shadow-sm',
    }

    const sizeClasses = {
      sm: 'text-sm px-3 py-1.5 gap-1.5 min-h-[32px]',
      md: 'text-sm px-4 py-2 gap-2 min-h-[40px]',
      lg: 'text-base px-6 py-3 gap-2 min-h-[48px]',
    }

    return (
      <button
        ref={ref}
        className={cn(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          className
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <LoadingSpinner size={size} />
            <span>{children}</span>
          </>
        ) : (
          <>
            {icon && iconPosition === 'left' && icon}
            {children && <span>{children}</span>}
            {icon && iconPosition === 'right' && icon}
          </>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'

// Loading spinner
function LoadingSpinner({ size }: { size: 'sm' | 'md' | 'lg' }) {
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  }

  return (
    <svg
      className={cn('animate-spin', sizeClasses[size])}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}

// Pill button variant (for toggles and tabs)
interface PillButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean
  size?: 'sm' | 'md'
}

export const PillButton = forwardRef<HTMLButtonElement, PillButtonProps>(
  ({ className, active = false, size = 'md', children, ...props }, ref) => {
    const sizeClasses = {
      sm: 'text-xs px-2.5 py-1',
      md: 'text-sm px-3 py-1.5',
    }

    return (
      <button
        ref={ref}
        className={cn(
          'rounded-full font-medium transition-all duration-150',
          'focus:outline-none focus:ring-2 focus:ring-primary/50',
          sizeClasses[size],
          active
            ? 'bg-primary text-white shadow-sm'
            : 'bg-transparent text-text-muted hover:text-text-primary hover:bg-elevated',
          className
        )}
        {...props}
      >
        {children}
      </button>
    )
  }
)

PillButton.displayName = 'PillButton'

// Segmented Control (group of pill buttons)
interface SegmentedControlProps<T extends string> {
  options: { value: T; label: string }[]
  value: T
  onChange: (value: T) => void
  size?: 'sm' | 'md'
  className?: string
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
  className,
}: SegmentedControlProps<T>) {
  return (
    <div
      className={cn(
        'inline-flex gap-1 p-1 bg-elevated rounded-full',
        className
      )}
    >
      {options.map((option) => (
        <PillButton
          key={option.value}
          active={value === option.value}
          size={size}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </PillButton>
      ))}
    </div>
  )
}

// Floating Action Button
interface FABProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode
  label?: string
  position?: 'bottom-right' | 'bottom-center'
}

export const FAB = forwardRef<HTMLButtonElement, FABProps>(
  ({ className, icon, label, position = 'bottom-right', ...props }, ref) => {
    const positionClasses = {
      'bottom-right': 'fixed bottom-24 right-4',
      'bottom-center': 'fixed bottom-24 left-1/2 -translate-x-1/2',
    }

    return (
      <button
        ref={ref}
        className={cn(
          'z-40 flex items-center gap-2 bg-primary text-white rounded-full shadow-elevated',
          'transition-all duration-200 hover:shadow-lg hover:scale-105',
          'active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background',
          label ? 'px-5 py-3' : 'p-4',
          positionClasses[position],
          className
        )}
        {...props}
      >
        {icon}
        {label && <span className="font-medium">{label}</span>}
      </button>
    )
  }
)

FAB.displayName = 'FAB'
